// Starter puzzle catalogue: 20 hand-picked challenges, four per
// difficulty tier. Each one stamps a partial solution (anchored at a
// fixed top-left corner so the goal predicate can read the same
// cells the user touches) and asks the user to finish the wiring,
// stack, heat, etc. The `check` predicates are simple cell-state
// reads — `lit` material variants for signal puzzles, phase-change
// material reads for heat / freeze puzzles.
//
// Everything is laid out at 5× scale: each logic element (switch,
// LED, lamp) is a 5×5 block rather than a single cell, and the
// passive structures (basins, channels, screens, walls) are scaled
// to match. That makes the assemblies big enough to read and click
// comfortably at the default zoom. The engine flood-fills connected
// switch / LED cells, so a 5×5 switch toggles as one unit and a 5×5
// LED lights as one — the goal predicates read each block's centre
// cell. Functional single-cell parts (flip-flops, clocks) stay 1×1
// at scaled positions, since their I/O is neighbour-specific.

import type { SandboxLike } from "../sim/types";
import type { Puzzle, PuzzleCategory, PuzzleCell } from "./types";

// Element size: every block-built element is S×S cells.
const S = 5;

// An S×S block of `material` with its top-left at cell (x, y).
function block(
  x: number,
  y: number,
  material: string,
  state?: number,
): PuzzleCell[] {
  const out: PuzzleCell[] = [];
  for (let dy = 0; dy < S; dy++) {
    for (let dx = 0; dx < S; dx++) {
      out.push({ x: x + dx, y: y + dy, material, state });
    }
  }
  return out;
}

// A filled rectangle (inclusive) of `material`, in raw cell coords —
// for basins, channels, walls that aren't a single S×S block.
function rect(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  material: string,
  state?: number,
): PuzzleCell[] {
  const out: PuzzleCell[] = [];
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      out.push({ x, y, material, state });
    }
  }
  return out;
}

// Centre cell of a block placed at (x, y) — the representative cell a
// goal predicate reads for that element.
const cx = (x: number) => x + Math.floor(S / 2);
const cy = (y: number) => y + Math.floor(S / 2);

// Helper: read a cell's current material id-string. Returns the
// empty string when the cell is empty / out-of-bounds, so the goal
// predicates can compare with `=== "led_lit"` etc.
function materialAt(sandbox: SandboxLike, x: number, y: number): string {
  const w = sandbox.width();
  const h = sandbox.height();
  if (x < 0 || y < 0 || x >= w || y >= h) return "";
  const m = sandbox.snapshotMaterials()[y * w + x] >>> 0;
  if (m === 0) return "";
  for (const name of REVERSE_LOOKUP) {
    if (sandbox.materialId(name) === m) return name;
  }
  return "";
}

// All material id-strings that puzzle predicates compare against.
// Keep this list synced with the goals below so `materialAt` can
// reverse-look them up.
const REVERSE_LOOKUP = [
  "led",
  "led_lit",
  "signal_lamp",
  "signal_lamp_lit",
  "copper_wire",
  "copper_wire_lit",
  "switch_block",
  "switch_block_lit",
  "and_gate",
  "or_gate",
  "xor_gate",
  "not_gate",
  "clock",
  "clock_lit",
  "d_flipflop",
  "d_flipflop_lit",
  "water",
  "ice",
  "steam",
  "smoke",
  "fire",
  "wall",
  "screen_pixel",
  "screen_pixel_lit",
  "screen_wall",
  "screen_food",
  "wood",
  "lava",
  "stone",
  "sand",
  "hot_basalt",
  "basalt",
  "vinegar",
  "baking_soda",
  "carbon_dioxide",
  "kerosene",
  "smoke",
];

// Count cells of any of `names` inside the inclusive rect, sampling
// every `step` cells (keeps big-region checks cheap).
function countIn(
  s: SandboxLike,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  names: string[],
  step = 2,
): number {
  let n = 0;
  for (let y = y0; y <= y1; y += step) {
    for (let x = x0; x <= x1; x += step) {
      if (names.includes(materialAt(s, x, y))) n++;
    }
  }
  return n;
}

// Convenience: true iff the block centred on a block placed at
// (x, y) shows the powered `_lit` variant of `base`.
function blockLit(
  s: SandboxLike,
  x: number,
  y: number,
  base: string,
): boolean {
  return materialAt(s, cx(x), cy(y)) === `${base}_lit`;
}

// Is there a clock cell (lit or not) 4-adjacent to the block at
// (x, y)? Used by the oscillator-style puzzles.
function clockTouchingBlock(s: SandboxLike, x: number, y: number): boolean {
  for (let dy = -1; dy <= S; dy++) {
    for (let dx = -1; dx <= S; dx++) {
      const inside = dx >= 0 && dx < S && dy >= 0 && dy < S;
      const edge =
        !inside && dx >= -1 && dx <= S && dy >= -1 && dy <= S;
      if (!edge) continue;
      const m = materialAt(s, x + dx, y + dy);
      if (m === "clock" || m === "clock_lit") return true;
    }
  }
  return false;
}

// =======================================================================
// EASY
// =======================================================================

const EASY_LIGHT_AN_LED: Puzzle = {
  id: "easy-light-an-led",
  category: "easy",
  name: "Light an LED",
  quest:
    "A switch and an LED sit a short gap apart. Connect them with copper_wire and turn the switch on so the LED lights.",
  hint: "Draw a line of copper_wire from the switch to the LED, then double-click the switch.",
  start: [...block(20, 20, "switch_block"), ...block(50, 20, "led")],
  check: (s) => blockLit(s, 50, 20, "led"),
};

const EASY_FILL_THE_POOL: Puzzle = {
  id: "easy-fill-the-pool",
  category: "easy",
  name: "Fill the pool",
  quest:
    "A stone basin sits empty. Pour water in until the whole basin is full.",
  hint: "Paint water above the basin and let gravity fill it.",
  start: [
    ...rect(20, 50, 24, 64, "stone"), // left wall
    ...rect(50, 50, 54, 64, "stone"), // right wall
    ...rect(20, 60, 54, 64, "stone"), // floor
  ],
  check: (s) => {
    // Interior is cols 25..49, rows 50..59 — sample a coarse grid.
    for (let y = 52; y <= 58; y += 3) {
      for (let x = 27; x <= 47; x += 5) {
        if (materialAt(s, x, y) !== "water") return false;
      }
    }
    return true;
  },
};

const EASY_MELT_THE_ICE: Puzzle = {
  id: "easy-melt-the-ice",
  category: "easy",
  name: "Melt the ice",
  quest: "A block of ice sits on the ground. Warm it until it turns to water.",
  hint: "The heat tool above ~273 K, or a few fire cells nearby, melts ice into water.",
  start: [...block(90, 30, "ice")],
  check: (s) => materialAt(s, cx(90), cy(30)) === "water",
};

const EASY_TWO_LEDS: Puzzle = {
  id: "easy-two-leds",
  category: "easy",
  name: "Two for one",
  quest:
    "One switch, two LEDs. Wire the switch so BOTH LEDs light from the single switch.",
  hint: "Copper_wire branches freely — run one line to each LED, then flip the switch.",
  start: [
    ...block(20, 90, "switch_block"),
    ...block(50, 80, "led"),
    ...block(50, 100, "led"),
  ],
  check: (s) => blockLit(s, 50, 80, "led") && blockLit(s, 50, 100, "led"),
};

// =======================================================================
// MEDIUM
// =======================================================================

const MEDIUM_BOIL_WATER: Puzzle = {
  id: "medium-boil-water",
  category: "medium",
  name: "Boil it off",
  quest:
    "Water sits in a stone pot. Heat it past boiling until the surface turns to steam.",
  hint: "Lava under the pot, or the heat tool straight on the water, pushes it past 373 K.",
  start: [
    ...rect(30, 40, 34, 54, "stone"), // left wall
    ...rect(55, 40, 59, 54, "stone"), // right wall
    ...rect(30, 50, 59, 54, "stone"), // floor
    ...rect(35, 42, 54, 49, "water"), // water body
  ],
  check: (s) => {
    // Any surface cell turning to steam counts.
    for (let x = 37; x <= 52; x += 3) {
      if (materialAt(s, x, 42) === "steam") return true;
    }
    return false;
  },
};

const MEDIUM_HEAT_SNOW: Puzzle = {
  id: "medium-heat-snow",
  category: "medium",
  name: "Heat the snow",
  quest:
    "A bowl of snow. Heat it all the way: first it melts to water, then the water boils. Get steam rising off the pile.",
  hint: "Snow melts above 0 °C (273 K) and water boils above 100 °C (373 K) — so push the heat tool well past boiling, or set lava under the bowl.",
  start: [
    ...rect(60, 42, 64, 57, "stone"), // left wall
    ...rect(86, 42, 90, 57, "stone"), // right wall
    ...rect(60, 55, 90, 58, "stone"), // floor
    ...rect(65, 44, 85, 54, "snow"), // snow pile
  ],
  check: (s) => {
    // Steam at the surface means the snow melted *and* the melt boiled.
    for (let x = 67; x <= 83; x += 3) {
      if (materialAt(s, x, 44) === "steam") return true;
    }
    return false;
  },
};

const MEDIUM_BOTTLE_VOLCANO: Puzzle = {
  id: "medium-bottle-volcano",
  category: "medium",
  name: "Bottle volcano",
  quest:
    "A bottle with baking soda in the bottom. Pour vinegar in and make it erupt — get the CO₂ fizz to blast out the top of the neck.",
  hint: "Vinegar (a weak acid) + baking soda react instantly to release carbon dioxide; the gas shoots straight up the narrow neck. Paint vinegar down into the opening.",
  start: [
    ...rect(40, 64, 70, 67, "stone"), // base floor
    ...rect(40, 38, 43, 64, "stone"), // left wall
    ...rect(67, 38, 70, 64, "stone"), // right wall
    ...rect(43, 38, 49, 41, "stone"), // left shoulder
    ...rect(61, 38, 67, 41, "stone"), // right shoulder
    ...rect(48, 28, 50, 38, "stone"), // left neck wall
    ...rect(60, 28, 62, 38, "stone"), // right neck wall (opening x51..59)
    ...rect(45, 56, 65, 63, "baking_soda"), // baking soda bed
  ],
  check: (s) => {
    // CO2 risen above the neck top (y < 27) means it erupted out.
    for (let x = 48; x <= 62; x += 2) {
      for (let y = 18; y <= 26; y++) {
        if (materialAt(s, x, y) === "carbon_dioxide") return true;
      }
    }
    return false;
  },
};

const MEDIUM_KEROSENE_FIRE: Puzzle = {
  id: "medium-kerosene-fire",
  category: "medium",
  name: "Set it alight",
  quest:
    "A tray of kerosene sits cold. Set it alight — get the pool burning so flames spread across it.",
  hint: "Kerosene ignites around 493 K. Crank the heat tool high over the pool (or drop fire onto it); once one patch catches, the flames race across the rest.",
  start: [
    ...rect(40, 60, 80, 63, "stone"), // tray floor
    ...rect(40, 50, 43, 60, "stone"), // left wall
    ...rect(77, 50, 80, 60, "stone"), // right wall
    ...rect(44, 54, 76, 59, "kerosene"), // kerosene pool
  ],
  check: (s) =>
    // A real spread (several fire cells across the pool), not a single
    // dropped flame.
    countIn(s, 44, 50, 76, 59, ["fire"], 1) >= 3,
};

const MEDIUM_BUILD_AND: Puzzle = {
  id: "medium-build-and",
  category: "medium",
  name: "Wire an AND",
  quest:
    "Two switches and a lamp. Drop an and_gate so the lamp lights only when BOTH switches are on.",
  hint: "An AND outputs high only when both inputs are. One switch is already on — wire both through the gate and flip the second.",
  start: [
    ...block(20, 70, "switch_block_lit"), // already on
    ...block(20, 95, "switch_block"), // user flips this
    ...block(55, 82, "signal_lamp"),
  ],
  check: (s) => blockLit(s, 55, 82, "signal_lamp"),
};

const MEDIUM_BUILD_OR: Puzzle = {
  id: "medium-build-or",
  category: "medium",
  name: "Wire an OR",
  quest:
    "Two switches (both off) and a lamp. Wire an or_gate so the lamp lights when EITHER switch is on.",
  hint: "Flip just one switch — with a correct OR the lamp should light from a single input.",
  start: [
    ...block(120, 70, "switch_block"),
    ...block(120, 95, "switch_block"),
    ...block(155, 82, "signal_lamp"),
  ],
  check: (s) => blockLit(s, 155, 82, "signal_lamp"),
};

const MEDIUM_FREEZE_A_DROP: Puzzle = {
  id: "medium-freeze-a-drop",
  category: "medium",
  name: "Freeze a drop",
  quest: "A small pool of water. Chill it until the whole pool becomes ice.",
  hint: "The heat tool set below ~273 K (try 250 K) crystallises water into ice.",
  start: [
    ...rect(90, 70, 94, 79, "stone"),
    ...rect(110, 70, 114, 79, "stone"),
    ...rect(90, 78, 114, 79, "stone"),
    ...rect(95, 72, 109, 77, "water"),
  ],
  check: (s) => {
    for (let y = 73; y <= 76; y += 3) {
      for (let x = 97; x <= 107; x += 5) {
        if (materialAt(s, x, y) !== "ice") return false;
      }
    }
    return true;
  },
};

// =======================================================================
// DIFFICULT
// =======================================================================

const DIFFICULT_HALF_ADDER: Puzzle = {
  id: "difficult-half-adder",
  category: "difficult",
  name: "Sum and carry",
  quest:
    "Two input switches, two output lamps. Wire a half-adder: the sum lamp = XOR of the inputs, the carry lamp = AND.",
  hint: "XOR drives sum, AND drives carry. With one switch on, sum lights and carry stays dark.",
  start: [
    ...block(20, 40, "switch_block_lit"), // A = 1
    ...block(20, 65, "switch_block"), // B = 0
    ...block(55, 42, "signal_lamp"), // SUM
    ...block(55, 63, "signal_lamp"), // CARRY
  ],
  check: (s) =>
    blockLit(s, 55, 42, "signal_lamp") && !blockLit(s, 55, 63, "signal_lamp"),
};

const DIFFICULT_OSCILLATOR: Puzzle = {
  id: "difficult-oscillator",
  category: "difficult",
  name: "Make it blink",
  quest:
    "Build an oscillator: an LED that switches itself on and off. Drive it from a clock.",
  hint: "Place a clock cell next to a wire feeding the LED — the clock toggles every couple of ticks.",
  start: [...block(120, 40, "led")],
  check: (s) =>
    clockTouchingBlock(s, 120, 40) && materialAt(s, cx(120), cy(40)).startsWith("led"),
};

const DIFFICULT_NOT_INVERTER: Puzzle = {
  id: "difficult-not-inverter",
  category: "difficult",
  name: "Invert it",
  quest:
    "A switch (off) and a lamp. Wire a not_gate so the lamp is ON while the switch is OFF.",
  hint: "A NOT outputs high when its input is low — so with the switch off and the NOT wired in, the lamp lights.",
  start: [...block(120, 95, "switch_block"), ...block(155, 95, "signal_lamp")],
  check: (s) => blockLit(s, 155, 95, "signal_lamp"),
};

const DIFFICULT_THREE_INPUT_AND: Puzzle = {
  id: "difficult-three-input-and",
  category: "difficult",
  name: "All three",
  quest:
    "Three switches and a lamp. Chain two and_gates so the lamp lights only when ALL three switches are on.",
  hint: "AND is two-input — feed two switches into the first gate, then that result + the third into the second gate.",
  start: [
    ...block(20, 115, "switch_block_lit"),
    ...block(20, 135, "switch_block_lit"),
    ...block(20, 155, "switch_block"),
    ...block(55, 135, "signal_lamp"),
  ],
  check: (s) => blockLit(s, 55, 135, "signal_lamp"),
};

// =======================================================================
// HARDCORE
// =======================================================================

const HARDCORE_DFF_LATCH: Puzzle = {
  id: "hardcore-dff-latch",
  category: "hardcore",
  name: "Latch a bit",
  quest:
    "A D-flip-flop sits unwired. Drive its D input from a switch and its CLK from a clock, then latch a 1 into it.",
  hint: "FF: D = west, CLK = south, Q = north. The cell swaps to d_flipflop_lit once it stores a 1.",
  // The flip-flop is a functional single cell — keep it 1×1 at a
  // scaled position rather than a 5×5 block.
  start: [{ x: 60, y: 60, material: "d_flipflop" }],
  check: (s) => materialAt(s, 60, 60) === "d_flipflop_lit",
};

const HARDCORE_FREEZE_THE_RIVER: Puzzle = {
  id: "hardcore-freeze-the-river",
  category: "hardcore",
  name: "Freeze the river",
  quest:
    "Water runs through a long channel. Convert every water cell in the channel to ice.",
  hint: "Sweep the cold heat-tool (≈250 K) along the whole channel.",
  start: [
    ...rect(60, 80, 119, 84, "stone"), // top bank
    ...rect(60, 90, 119, 94, "stone"), // bottom bank
    ...rect(60, 85, 119, 89, "water"), // the river
  ],
  check: (s) => {
    for (let x = 62; x <= 117; x += 5) {
      if (materialAt(s, x, 87) !== "ice") return false;
    }
    return true;
  },
};

const HARDCORE_TOGGLE_FLIPFLOP: Puzzle = {
  id: "hardcore-toggle-flipflop",
  category: "hardcore",
  name: "Self-toggling bit",
  quest:
    "Make a flip-flop that flips every clock: wire D = NOT Q and feed CLK from a clock.",
  hint: "Tap Q (north) into a NOT, route the NOT's output back to D (west), drive CLK from a clock cell.",
  start: [{ x: 100, y: 110, material: "d_flipflop" }],
  // Goal: the FF has a clock on its CLK side and a NOT gate placed —
  // the toggling itself is observed as the FF visiting its lit state.
  check: (s) => {
    const clockBelow =
      materialAt(s, 100, 111) === "clock" ||
      materialAt(s, 100, 111) === "clock_lit";
    // A NOT gate somewhere in the 7×7 neighbourhood of the FF.
    let hasNot = false;
    for (let dy = -3; dy <= 3 && !hasNot; dy++) {
      for (let dx = -3; dx <= 3; dx++) {
        if (materialAt(s, 100 + dx, 110 + dy) === "not_gate") {
          hasNot = true;
          break;
        }
      }
    }
    return clockBelow && hasNot;
  },
};

const HARDCORE_STEAM_CHAMBER: Puzzle = {
  id: "hardcore-steam-chamber",
  category: "hardcore",
  name: "Pressure cooker",
  quest:
    "Seal water inside a stone chamber with lava beneath it and build up steam — fill the top half of the chamber with steam.",
  hint: "A fully-walled chamber traps the steam as the water boils, so it accumulates instead of escaping.",
  start: [
    ...rect(60, 120, 99, 124, "stone"), // top lid
    ...rect(60, 150, 99, 154, "stone"), // floor
    ...rect(60, 124, 64, 154, "stone"), // left wall
    ...rect(95, 124, 99, 154, "stone"), // right wall
    ...rect(65, 140, 94, 149, "water"), // water
    ...rect(65, 150, 94, 150, "lava"), // lava strip under floor edge
  ],
  check: (s) => {
    // Top interior band (rows 125..132) should hold steam.
    let steam = 0;
    for (let x = 67; x <= 92; x += 5) {
      for (let y = 126; y <= 132; y += 3) {
        if (materialAt(s, x, y) === "steam") steam++;
      }
    }
    return steam >= 6;
  },
};

// =======================================================================
// HYPER-EXTREME
// =======================================================================

const HYPEREXTREME_LIGHT_SCREEN: Puzzle = {
  id: "hyperextreme-light-screen",
  category: "hyper-extreme",
  name: "Light the screen",
  quest:
    "A 4×4 grid of big screen pixels is dark. Light every one (state ≥ 1) — by CPU writes, a wired source, or any means.",
  hint: "screen_pixel lights when its state byte is non-zero; the engine swaps it for a lit variant.",
  // 4×4 grid of S×S screen blocks at top-left (80, 30), block pitch S.
  start: (() => {
    const cells: PuzzleCell[] = [];
    for (let by = 0; by < 4; by++) {
      for (let bx = 0; bx < 4; bx++) {
        cells.push(...block(80 + bx * S, 30 + by * S, "screen_pixel"));
      }
    }
    return cells;
  })(),
  check: (s) => {
    const w = s.width();
    const ids = s.snapshotMaterials();
    const lit = [
      s.materialId("screen_pixel_lit"),
      s.materialId("screen_wall"),
      s.materialId("screen_food"),
    ].filter((id) => id !== 0);
    if (lit.length === 0) return false;
    for (let by = 0; by < 4; by++) {
      for (let bx = 0; bx < 4; bx++) {
        const px = cx(80 + bx * S);
        const py = cy(30 + by * S);
        const m = ids[py * w + px] >>> 0;
        if (!lit.includes(m)) return false;
      }
    }
    return true;
  },
};

const HYPEREXTREME_BUILD_CHIMNEY: Puzzle = {
  id: "hyperextreme-build-chimney",
  category: "hyper-extreme",
  name: "Smoke a chimney",
  quest:
    "Build a tall chimney over the fire and get steam or smoke to rise out of the top opening.",
  hint: "Two stone walls forming a flue, fire at the base, water near the heat — the convection carries the plume up.",
  start: [
    ...rect(150, 90, 154, 90, "fire"),
    ...rect(148, 91, 149, 110, "stone"), // left flue wall
    ...rect(155, 91, 156, 110, "stone"), // right flue wall
  ],
  check: (s) => {
    // A rising-fluid cell high up the flue (rows ≤ 96) means the
    // plume reached the top.
    for (let y = 90; y <= 96; y++) {
      const m = materialAt(s, 152, y);
      if (m === "steam" || m === "smoke" || m === "fire") return true;
    }
    return false;
  },
};

const HYPEREXTREME_CHECKERBOARD: Puzzle = {
  id: "hyperextreme-checkerboard",
  category: "hyper-extreme",
  name: "Checkerboard",
  quest:
    "A 4×4 grid of screen pixels. Light EXACTLY the checkerboard squares — every other pixel on, the rest dark.",
  hint: "Light blocks where (row + col) is even; leave the odd ones dark. A CPU loop or manual writes both work.",
  start: (() => {
    const cells: PuzzleCell[] = [];
    for (let by = 0; by < 4; by++) {
      for (let bx = 0; bx < 4; bx++) {
        cells.push(...block(150 + bx * S, 30 + by * S, "screen_pixel"));
      }
    }
    return cells;
  })(),
  check: (s) => {
    const w = s.width();
    const ids = s.snapshotMaterials();
    const litIds = [
      s.materialId("screen_pixel_lit"),
      s.materialId("screen_wall"),
      s.materialId("screen_food"),
    ].filter((id) => id !== 0);
    if (litIds.length === 0) return false;
    for (let by = 0; by < 4; by++) {
      for (let bx = 0; bx < 4; bx++) {
        const px = cx(150 + bx * S);
        const py = cy(30 + by * S);
        const m = ids[py * w + px] >>> 0;
        const wantLit = (bx + by) % 2 === 0;
        const isOn = litIds.includes(m);
        if (wantLit !== isOn) return false;
      }
    }
    return true;
  },
};

const HYPEREXTREME_LAMP_ROW: Puzzle = {
  id: "hyperextreme-lamp-row",
  category: "hyper-extreme",
  name: "Light the row",
  quest:
    "Four lamps in a row, one master switch. Wire them so a single switch lights all four at once.",
  hint: "Fan one powered wire out to every lamp — a power bus. Then flip the master switch.",
  start: [
    ...block(60, 170, "switch_block"),
    ...block(85, 170, "signal_lamp"),
    ...block(100, 170, "signal_lamp"),
    ...block(115, 170, "signal_lamp"),
    ...block(130, 170, "signal_lamp"),
  ],
  check: (s) =>
    blockLit(s, 85, 170, "signal_lamp") &&
    blockLit(s, 100, 170, "signal_lamp") &&
    blockLit(s, 115, 170, "signal_lamp") &&
    blockLit(s, 130, 170, "signal_lamp"),
};

// =======================================================================
// WILD BATCH — two more per category, leaning into physics, humour,
// and multi-stage builds.
// =======================================================================

// ---- EASY ----

const EASY_CAMPFIRE: Puzzle = {
  id: "easy-campfire",
  category: "easy",
  name: "Light the campfire",
  quest:
    "A neat little stack of logs is begging for a match. Set the wood alight and watch it catch.",
  hint: "Drop a few `fire` cells onto the logs — wood ignites on contact and the flames spread.",
  start: [
    ...rect(20, 50, 40, 51, "stone"), // hearth stones
    ...block(22, 44, "wood"),
    ...block(33, 44, "wood"),
    ...block(27, 38, "wood"),
  ],
  check: (s) =>
    // Any fire in the log pile means it caught.
    countIn(s, 20, 36, 40, 49, ["fire"], 1) >= 1,
};

const EASY_SNOWMAN: Puzzle = {
  id: "easy-snowman",
  category: "easy",
  name: "Build a snowman",
  quest:
    "One snowball is on the ground. Stack two more ice balls on top of it to finish the snowman.",
  hint: "Paint two more 5×5 balls of `ice`, each sitting on the one below.",
  start: [...block(50, 60, "ice")],
  check: (s) =>
    materialAt(s, cx(50), cy(60)) === "ice" &&
    materialAt(s, cx(50), cy(54)) === "ice" &&
    materialAt(s, cx(50), cy(48)) === "ice",
};

// ---- MEDIUM ----

const MEDIUM_VOLCANO: Puzzle = {
  id: "medium-volcano",
  category: "medium",
  name: "Lava meets the sea",
  quest:
    "A pool of molten lava sits in a stone tray. Quench it with water until it solidifies into rock.",
  hint: "Pour water onto the lava — it flashes to steam and the lava freezes into hot basalt that cools to stone.",
  start: [
    ...rect(70, 50, 110, 54, "stone"), // tray floor
    ...rect(70, 40, 74, 54, "stone"), // left wall
    ...rect(106, 40, 110, 54, "stone"), // right wall
    ...rect(75, 45, 105, 49, "lava"), // molten pool
  ],
  check: (s) =>
    // Enough of the lava body has turned to (hot) basalt.
    countIn(s, 75, 44, 105, 50, ["hot_basalt", "basalt"]) >= 18,
};

const MEDIUM_COMBINATION_LOCK: Puzzle = {
  id: "medium-combination-lock",
  category: "medium",
  name: "Combination lock",
  quest:
    "Two dials, one green unlock light. Wire it so the light comes on ONLY when the two dials disagree (one on, one off).",
  hint: "That's exactly XOR. One dial starts on, the other off — a correct XOR lights the lamp right away.",
  start: [
    ...block(120, 45, "switch_block_lit"), // dial A = on
    ...block(120, 65, "switch_block"), // dial B = off
    ...block(155, 55, "signal_lamp"), // unlock light
  ],
  check: (s) => blockLit(s, 155, 55, "signal_lamp"),
};

// ---- DIFFICULT ----

const DIFFICULT_FULL_ADDER: Puzzle = {
  id: "difficult-full-adder",
  category: "difficult",
  name: "Carry the one",
  quest:
    "Three inputs (A, B, carry-in) feed a sum lamp and a carry-out lamp. Build a full adder. The inputs are A=1, B=1, Cin=0 — so sum must be 0 and carry-out must be 1.",
  hint: "Two half-adders + an OR: sum = A⊕B⊕Cin, carry = (A·B)+(Cin·(A⊕B)). For 1,1,0 the carry lights and the sum stays dark.",
  start: [
    ...block(20, 80, "switch_block_lit"), // A = 1
    ...block(20, 100, "switch_block_lit"), // B = 1
    ...block(20, 120, "switch_block"), // Cin = 0
    ...block(55, 88, "signal_lamp"), // SUM
    ...block(55, 112, "signal_lamp"), // CARRY-OUT
  ],
  check: (s) =>
    blockLit(s, 55, 112, "signal_lamp") && !blockLit(s, 55, 88, "signal_lamp"),
};

const DIFFICULT_MUX: Puzzle = {
  id: "difficult-mux",
  category: "difficult",
  name: "Pick a lane",
  quest:
    "Two data inputs and a select dial feed one lamp. Build a 2:1 multiplexer so the lamp follows whichever input the select dial points at. Select is off, so the lamp should follow input 0 (which is on).",
  hint: "out = (D0 AND NOT sel) OR (D1 AND sel). With sel off and D0 on, the lamp lights.",
  start: [
    ...block(85, 80, "switch_block_lit"), // D0 = 1
    ...block(85, 100, "switch_block"), // D1 = 0
    ...block(85, 120, "switch_block"), // SEL = 0
    ...block(125, 100, "signal_lamp"), // OUT
  ],
  check: (s) => blockLit(s, 125, 100, "signal_lamp"),
};

// ---- HARDCORE ----

const HARDCORE_TWO_BIT_COUNTER: Puzzle = {
  id: "hardcore-two-bit-counter",
  category: "hardcore",
  name: "Count to three",
  quest:
    "A clock and two LEDs are waiting. Build a 2-bit binary counter (two flip-flops) so the LEDs count 00 → 01 → 10 → 11 → 00 as the clock ticks.",
  hint: "Bit 0 toggles every clock (D0 = NOT Q0). Bit 1 toggles when bit 0 falls (D1 = Q1 XOR Q0). Drive both CLKs from the clock.",
  start: [
    { x: 150, y: 110, material: "clock" },
    ...block(170, 85, "led"),
    ...block(190, 85, "led"),
  ],
  check: (s) => {
    const clock =
      materialAt(s, 150, 110) === "clock" ||
      materialAt(s, 150, 110) === "clock_lit";
    // At least two flip-flops wired into the work area, and the
    // machine is live (a flip-flop currently storing a 1).
    const ffCount = countIn(
      s,
      150,
      80,
      215,
      125,
      ["d_flipflop", "d_flipflop_lit"],
      1,
    );
    const ffLive = countIn(s, 150, 80, 215, 125, ["d_flipflop_lit"], 1) >= 1;
    return clock && ffCount >= 2 && ffLive;
  },
};

const HARDCORE_COMMUNICATING_VESSELS: Puzzle = {
  id: "hardcore-communicating-vessels",
  category: "hardcore",
  name: "Find your level",
  quest:
    "Two tall stone columns are joined by a pipe at the bottom. Pour water into the LEFT column and let it flow under the divider until the RIGHT column fills to the same level.",
  hint: "Water seeks its own level — pour enough into the left and it rises in the right on its own.",
  start: [
    ...rect(20, 196, 76, 200, "stone"), // floor
    ...rect(20, 140, 24, 196, "stone"), // left outer wall
    ...rect(72, 140, 76, 196, "stone"), // right outer wall
    ...rect(46, 140, 50, 190, "stone"), // centre divider (gap at bottom)
  ],
  check: (s) =>
    // Water has risen on BOTH sides above row 175 → it equalised.
    materialAt(s, 35, 175) === "water" && materialAt(s, 61, 175) === "water",
};

// ---- HYPER-EXTREME ----

const HYPEREXTREME_HOURGLASS: Puzzle = {
  id: "hyperextreme-hourglass",
  category: "hyper-extreme",
  name: "Run the hourglass",
  quest:
    "Sand fills the top chamber of an hourglass. Let it run: the sand should pour through the narrow neck and pile up in the bottom chamber.",
  hint: "Sand is a powder — it falls on its own. If it jams, widen the neck or nudge the pile so it keeps streaming down.",
  start: [
    // Top chamber walls + a floor with a central neck gap (x108..113).
    ...rect(90, 130, 94, 155, "stone"),
    ...rect(126, 130, 130, 155, "stone"),
    ...rect(95, 155, 107, 156, "stone"),
    ...rect(114, 155, 125, 156, "stone"),
    // Bottom chamber.
    ...rect(90, 160, 94, 200, "stone"),
    ...rect(126, 160, 130, 200, "stone"),
    ...rect(95, 196, 125, 200, "stone"),
    // The sand charge in the top chamber.
    ...rect(96, 140, 124, 154, "sand"),
  ],
  check: (s) =>
    // Sand has accumulated in the bottom chamber.
    countIn(s, 96, 185, 124, 195, ["sand"]) >= 30,
};

const HYPEREXTREME_FORGE_ISLAND: Puzzle = {
  id: "hyperextreme-forge-island",
  category: "hyper-extreme",
  name: "Forge an island",
  quest:
    "A wide lake of lava churns in a stone basin. Solidify it into a landmass of rock — at least a good chunk of the lake must turn to basalt.",
  hint: "Sweep water across the whole surface to quench it fast (it also cools on its own, but that's slow). Steam will billow as it sets.",
  start: [
    ...rect(140, 196, 220, 200, "stone"), // basin floor
    ...rect(140, 170, 144, 196, "stone"), // left wall
    ...rect(216, 170, 220, 196, "stone"), // right wall
    ...rect(145, 178, 215, 194, "lava"), // the lava lake
  ],
  check: (s) =>
    countIn(s, 145, 176, 215, 195, ["hot_basalt", "basalt"]) >= 60,
};

// =======================================================================

const ALL_PUZZLES: Puzzle[] = [
  // Easy
  EASY_LIGHT_AN_LED,
  EASY_FILL_THE_POOL,
  EASY_MELT_THE_ICE,
  EASY_TWO_LEDS,
  EASY_CAMPFIRE,
  EASY_SNOWMAN,
  // Medium
  MEDIUM_BOIL_WATER,
  MEDIUM_BUILD_AND,
  MEDIUM_BUILD_OR,
  MEDIUM_FREEZE_A_DROP,
  MEDIUM_VOLCANO,
  MEDIUM_COMBINATION_LOCK,
  MEDIUM_HEAT_SNOW,
  MEDIUM_BOTTLE_VOLCANO,
  MEDIUM_KEROSENE_FIRE,
  // Difficult
  DIFFICULT_HALF_ADDER,
  DIFFICULT_OSCILLATOR,
  DIFFICULT_NOT_INVERTER,
  DIFFICULT_THREE_INPUT_AND,
  DIFFICULT_FULL_ADDER,
  DIFFICULT_MUX,
  // Hardcore
  HARDCORE_DFF_LATCH,
  HARDCORE_FREEZE_THE_RIVER,
  HARDCORE_TOGGLE_FLIPFLOP,
  HARDCORE_STEAM_CHAMBER,
  HARDCORE_TWO_BIT_COUNTER,
  HARDCORE_COMMUNICATING_VESSELS,
  // Hyper-extreme
  HYPEREXTREME_LIGHT_SCREEN,
  HYPEREXTREME_BUILD_CHIMNEY,
  HYPEREXTREME_CHECKERBOARD,
  HYPEREXTREME_LAMP_ROW,
  HYPEREXTREME_HOURGLASS,
  HYPEREXTREME_FORGE_ISLAND,
];

export function puzzlesByCategory(c: PuzzleCategory): Puzzle[] {
  return ALL_PUZZLES.filter((p) => p.category === c);
}

export function getAllPuzzles(): Puzzle[] {
  return ALL_PUZZLES;
}

export function findPuzzle(id: string): Puzzle | undefined {
  return ALL_PUZZLES.find((p) => p.id === id);
}
